import React, { useState, useEffect, useRef } from 'react';
import { NavLink } from 'react-router-dom';
import { QRCodeCanvas } from 'qrcode.react';
import Layout from './Layout';
import { useTheme } from '../context/ThemeContext';
import Swal from 'sweetalert2';
import axios from 'axios';

const Visitor = () => {
  const { isDark } = useTheme();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const sidebarRef = useRef();
  const [qrOpen, setQrOpen] = useState(false);
  const qrRef = useRef();
  const qrCanvasRef = useRef();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [stats, setStats] = useState({
    total: 0,
    thisMonth: 0,
    thisWeek:0,
    inFollowUp: 0,
    converted: 0
  });
  const [visitors, setVisitors] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });
  const [filters, setFilters] = useState({
    visitor_type: '',
    status: '',
    search: '',
    startDate: '',
    endDate: '',
  });

  
const Toast = Swal.mixin({
  toast: true,
  position: 'top-end',
  showConfirmButton: false,
  timer: 3000,
  timerProgressBar: true
});

  // API Base URL - should be configured in environment
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://rccgtr.net/api';
  
  // Get system IP for QR code (test mode)
  // const getSystemIP = () => {
  //   // For test mode, use a common local network IP
  //   // In production, this should come from environment variables
  //   return '172.16.159.119:3000';
  // };

  const getSystemIP = () => {
    // Use environment variable in production, fallback to local IP for development
    if (process.env.NODE_ENV === 'production') {
      // For production, use environment variables
      return process.env.REACT_APP_PRODUCTION_URL || 
             process.env.REACT_APP_API_BASE_URL || 
             window.location.hostname;
    } else {
      // For development, use the hardcoded IP or environment variable
      return process.env.REACT_APP_DEV_URL || '172.16.159.119:3000';
    }
  };

  const handleDownloadQR = () => {
    try {
      const canvas = qrCanvasRef.current?.querySelector('canvas');
      if (!canvas) return;
      const link = document.createElement('a');
      link.download = 'visitor-form-qr.png';
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (_) {}
  };

  useEffect(() => {
    loadStats();
    loadVisitors();
  }, [pagination.page, filters]);

  const loadStats = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_BASE_URL}/visitors/stats`);
      
      if (data.success) {
        setStats({
          total: data.data.total,
          thisMonth: data.data.thisMonth,
          thisWeek: data.data.thisWeek,
          inFollowUp: data.data.statusBreakdown.find(item => item.status === 'follow_up')?.count || 0,
          converted: data.data.statusBreakdown.find(item => item.status === 'converted')?.count || 0
        });
      } else {
        Toast.fire({
          icon: 'error',
          title: 'Oops...!',
          text: 'Failed to load statistics',
          timer: 2000,
          showConfirmButton: false
        });
        // setMessage('Failed to load statistics');
      }
    } catch (error) {
      console.error('Error loading statistics:', error);
      Toast.fire({
        icon: 'error',
        title: 'Oops...!',
        text: 'Error loading statistics',
        timer: 2000,
        showConfirmButton: false
      });
      // setMessage('Error loading statistics');
    } finally {
      setLoading(false);
    }
  };

  const loadVisitors = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      });

      const { data } = await axios.get(`${API_BASE_URL}/visitors`, { params: Object.fromEntries(params) });
      
      if (data.success) {
        setVisitors(data.data);
        setPagination(prev => ({
          ...prev,
          total: data.pagination.total,
          pages: data.pagination.pages
        }));
      } else {
        Toast.fire({
          icon: 'error',
          title: 'Oops...!',
          text: 'Failed to load guests',
          timer: 2000,
          showConfirmButton: false
        });
        // setMessage('Failed to load guests');
      }
    } catch (error) {
      console.error('Error loading guests:', error);
      Toast.fire({
        icon: 'error',
        title: 'Error loading Guests',
        timer: 2000,
        showConfirmButton: false
      });
      // setMessage('Error loading Guests');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const getVisitorTypeName = (type) => {
    const names = {
      'first_timer': 'First Timer',
      'returning_member': 'Returning Member'
    };
    return names[type] || type;
  };

  const getStatusBadge = (status) => {
    const badges = {
      'new': 'bg-blue-300 text-blue-800',
      'follow_up': 'bg-yellow-300 text-yellow-800',
      'converted': 'bg-green-300 text-green-800',
      'lost': 'bg-red-300 text-red-800'
    };
    return badges[status] || 'bg-gray-300 text-gray-800';
  };

  const getStatusName = (status) => {
    const names = {
      'new': 'New',
      'follow_up': 'To follow-up',
      'converted': 'Converted',
      'lost': 'Lost'
    };
    return names[status] || status;
  };

  useEffect(() => {
    const handler = e => {
      if (sidebarOpen && sidebarRef.current && !sidebarRef.current.contains(e.target)) {
        setSidebarOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [sidebarOpen]);

  useEffect(() => {
    if (showAlert) {
      const timeout = setTimeout(() => setShowAlert(false), 5000);
      return () => clearTimeout(timeout);
    }
  }, [showAlert]);

  // Close QR popup when clicking outside
  useEffect(() => {
    if (!qrOpen) return;
    const handler = e => {
      if (qrRef.current && !qrRef.current.contains(e.target)) {
        setQrOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [qrOpen]);

  const triggerAlert = () => {
    setShowAlert(true);
  };

  return (
    		<Layout title="Guest Management">
        {showAlert && (
          <div className="fixed top-5 left-1/2 transform -translate-x-1/2 bg-green-500 text-white px-6 py-3 rounded shadow animate-bounce z-50">
            🎉 Guest added successfully!
          </div>
        )}
        
        {/* Message Display */}
        {message && (
          <div className={`mb-4 p-4 rounded-lg ${
            message.includes('Failed') || message.includes('Error')
              ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
              : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
          }`}>
            {message}
            <button 
              onClick={() => setMessage('')}
              className="float-right font-bold"
            >
              ×
            </button>
          </div>
        )}
        {/* Main Content */}
        <div>
          <div className="md:flex md:justify-between md:w-full  md:flex-row md:gap-80 flex flex-col space-y-3">
            <div>
              <h1
                className={` md:text-left mt-20 ml-20 md:ml-20 text-xl md:text-2xl font-bold ${
                  isDark ? 'text-indigo-100' : 'text-indigo-900'
                }`}
              >
                Guest Management
              </h1>
              <p
                className={` md:text-left ml-5 md:ml-20 text-center  mt-2 md:text-sm font-semibold ${
                  isDark ? 'text-indigo-100' : 'text-gray-600'
                }`}
              >
                Track and manage church Guests
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <NavLink to="/guestsRegistration">
                <h1
                  className={`  md:text-left text-center ml-10 md:ml-72 md:mx-0  md:mt-20 mt-2 p-2 rounded-md md:text-sm font-semibold ${
                    isDark ? 'bg-indigo-200 text-gray-900' : 'bg-indigo-700 text-white'
                  }`}
                >
                  Add New Guest
                </h1>
              </NavLink>
              <button
                onClick={() => setQrOpen(true)}
                className={`md:mt-20 mt-2 p-2 rounded-md md:text-sm font-semibold border-2 border-green-700 ml-2 shadow hover:bg-green-700 hover:text-white transition ${
                  isDark ? 'bg-gray-800 text-yellow-400' : 'bg-white text-green-800'
                }`}
              >
                Open QR Code
              </button>
            </div>
            {qrOpen && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
                <div
                  ref={qrRef}
                  className={`${isDark ? 'bg-gray-900 text-indigo-100' : 'bg-white'} p-8 rounded-2xl shadow-xl`}
                >
                  <div ref={qrCanvasRef}
                   className={`${isDark ? 'bg-gray-800' : 'bg-gray-100'} p-6 rounded-2xl shadow flex flex-col items-center`}>
                    <QRCodeCanvas
                      value={`http://${getSystemIP()}/guestsRegistration`}
                      size={200}
                      includeMargin
                      bgColor={isDark ? "#ffffff" : "#ffffff"}   // ✅ background matches Students QR
                      fgColor={isDark ? "#111827" : "#111827"}   // ✅ dark vs light contrast
                    />
                  </div>
                  <p className="mt-6 text-center text-lg font-semibold dark:text-green-200 text-green-900">
                    Scan to add a new Guest
                  </p>
                  <button
                    onClick={() => setQrOpen(false)}
                    className="mt-6 px-6 py-3 rounded bg-green-700 text-white hover:bg-green-800 transition text-base font-semibold"
                  >
                    Close
                  </button>
                  <button
                    onClick={handleDownloadQR}
                    className="mt-3 px-6 py-2 rounded bg-indigo-600 text-white hover:bg-indigo-700 transition text-sm font-semibold"
                  >
                    Download QR
                  </button>
                </div>
              </div>
            )}
          </div>
          <div
            className={`md:flex md:flex-row flex flex-col space-y-3 ml-5 animate-fade-in-up md:w-auto w-full md:space-y-0 p-5 md:ml-10 mt-4 rounded-md  md:gap-4 mb-8 ${
              isDark ? 'bg-gray-700' : ' bg-white'
            }`}
          >
            <div className="p-6 rounded-lg animate-fade-in-up shadow text-white md:w-56 bg-gradient-to-r from-blue-500 to-blue-900">
              <p className="text-sm">Total Guests</p>
              <h2 className="text-3xl font-bold mt-2">{stats.total}</h2>
            </div>
            <div className="p-6 rounded-lg animate-fade-in-up shadow text-white md:w-56 bg-gradient-to-r from-green-500 to-green-900">
              <p className="text-sm">This Month</p>
              <h2 className="text-3xl font-bold mt-2">{stats.thisMonth}</h2>
            </div>{' '}
            <div className="p-6 rounded-lg animate-fade-in-up shadow text-white md:w-56 bg-gradient-to-r from-orange-500 to-orange-900">
              <p className="text-sm">This Week</p>
              <h2 className="text-3xl font-bold mt-2">{stats.thisWeek}</h2>
            </div>{' '}
            <div className="p-6 rounded-lg shadow animate-fade-in-up text-white md:w-56 bg-gradient-to-r from-purple-500 to-purple-900">
              <p className="text-sm">Converted to Members</p>
              <h2 className="text-3xl font-bold mt-2">{stats.converted}</h2>
            </div>
          </div>
          {/* Visitors Table */}
          <div
            className={` w-72 ml-10 md:w-auto md:overflow-auto p-6 rounded-lg shadow overflow-x-auto ${
              isDark ? 'bg-gray-700' : 'bg-white'
            }`}
          >
            <h3 className="text-xl font-semibold mb-4">Visitors</h3>
            <div className="flex gap-2 mb-4">
         
              <label className="block text-sm mb-1">From</label>
              <input
                type="date"
                value={filters.startDate}
                onChange={e => handleFilterChange('startDate', e.target.value)}
                className={`${isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'} border rounded px-3 py-2`}
              />
            
           
              <label className="block text-sm mb-1">To</label>
              <input
                type="date"
                value={filters.endDate}
                onChange={e => handleFilterChange('endDate', e.target.value)}
                className={`${isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'} border rounded px-3 py-2`}
              />
          

              <select 
                value={filters.visitor_type} 
                onChange={(e) => handleFilterChange('visitor_type', e.target.value)}
                className={`${isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'} border rounded px-2 py-1`}
              >
                <option value="">All Types</option>
                <option value="first_timer">First Timer</option>
                <option value="returning_member">Returning Member</option>
              </select>
              
              <input
                type="text"
                placeholder="Search visitors..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className={`${isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'} border rounded px-2 py-1`}
              />
            </div>

            {loading ? (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              </div>
            ) : visitors.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No visitors found
              </div>
            ) : (
              <>
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className={`text-left ${isDark ? 'bg-gray-700' : 'bg-white'}`}>
                      <th className="p-6 text-left">SN</th>
                      <th className="p-6">Name</th>
                      <th className="p-6">Contact</th>
                      <th className="p-6">Visit Date</th>
                      <th className="p-6">Type</th>
                      <th className="p-6">Source</th>
                      <th className="p-6">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visitors.map((visitor,i) => (
                      <tr key={visitor.id} className="border-b border-gray-200 dark:border-gray-600">
                        <td className="p-4">{1 + i}</td>
                        <td className="p-4">{visitor.first_name} {visitor.last_name}</td>
                        <td className="p-4">{visitor.phone || visitor.email}</td>
                        <td className="p-4">{new Date(visitor.visit_date).toLocaleDateString()}</td>
                        <td className="p-4">{getVisitorTypeName(visitor.visitor_type)}</td>
                        <td className="p-4">{visitor.source || 'N/A'}</td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded text-xs ${getStatusBadge(visitor.status || 'new')}`}>
                            {getStatusName(visitor.status || 'new')}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                
                {/* Pagination */}
                {pagination.pages > 1 && (
                  <div className="flex justify-center items-center gap-2 mt-4">
                    <button
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page === 1}
                      className="px-3 py-1 bg-indigo-600 text-white rounded disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <span className="px-3 py-1">
                      Page {pagination.page} of {pagination.pages}
                    </span>
                    <button
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page === pagination.pages}
                      className="px-3 py-1 bg-indigo-600 text-white rounded disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        {/* Custom animation style */}
        <style>{`
  @keyframes fade-in-up {
    0% {
      opacity: 0;
      transform: translateY(20px);
    }
    100% {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .animate-fade-in-up {
    animation: fade-in-up 0.6s ease-out;
  }
`}</style>
      
    </Layout>
  );
};

export default Visitor;
